/**
 * Documentation Service
 *
 * Provides access to the Firestore-based documentation system.
 * Documentation is platform-level (not company-scoped) and supports
 * context-aware help via contextTriggers.
 */

// Import types only - functions come from shell's firestoreHelpers to avoid scope mismatch
import type { Firestore } from 'firebase/firestore';

import {
  DocumentationDoc,
  DocumentationCategory,
  ToolId,
  DocumentationByCategory,
  CATEGORY_LABELS,
  CATEGORY_ORDER,
} from '../types/documentation.types';

const COLLECTION_NAME = 'documentation';

// Type for the helpers bundle from shell
interface FirestoreHelpers {
  collection: typeof import('firebase/firestore').collection;
  query: typeof import('firebase/firestore').query;
  where: typeof import('firebase/firestore').where;
  getDocs: typeof import('firebase/firestore').getDocs;
  doc: typeof import('firebase/firestore').doc;
  getDoc: typeof import('firebase/firestore').getDoc;
  orderBy: typeof import('firebase/firestore').orderBy;
  Timestamp: typeof import('firebase/firestore').Timestamp;
  setDoc: typeof import('firebase/firestore').setDoc;
  serverTimestamp: typeof import('firebase/firestore').serverTimestamp;
}

interface FirebaseBundle {
  db: Firestore;
  helpers: FirestoreHelpers;
}

// Async Firebase loading for Module Federation compatibility
let cachedBundle: FirebaseBundle | null = null;
let bundlePromise: Promise<FirebaseBundle> | null = null;

async function getFirebase(): Promise<FirebaseBundle> {
  if (cachedBundle) return cachedBundle;

  if (!bundlePromise) {
    bundlePromise = (async (): Promise<FirebaseBundle> => {
      // @ts-ignore - Module Federation dynamic import
      const firebase = await import('shell/Firebase');
      if (!firebase.db) {
        throw new Error('Firebase db not available from shell');
      }
      if (!firebase.firestoreHelpers) {
        throw new Error('Firebase firestoreHelpers not available from shell');
      }
      cachedBundle = {
        db: firebase.db as Firestore,
        helpers: firebase.firestoreHelpers as FirestoreHelpers,
      };
      return cachedBundle;
    })();
  }

  return bundlePromise;
}

/**
 * Convert Firestore timestamps to JavaScript Dates
 * Uses duck typing since Timestamp class may be from different scope
 */
function convertTimestamps(data: any): any {
  const converted = { ...data };

  Object.keys(converted).forEach((key) => {
    const value = converted[key];
    // Use duck typing - check for toDate method instead of instanceof
    if (value && typeof value === 'object' && typeof value.toDate === 'function') {
      converted[key] = value.toDate();
    }
  });

  return converted;
}

/**
 * Documentation Service for context-aware help
 */
export class DocumentationService {
  /**
   * Get a single documentation doc by its Firestore document ID
   */
  static async getDoc(documentId: string): Promise<DocumentationDoc | null> {
    try {
      const { db, helpers } = await getFirebase();
      const { doc, getDoc } = helpers;
      const docRef = doc(db, COLLECTION_NAME, documentId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return {
        id: docSnap.id,
        ...convertTimestamps(docSnap.data()),
      } as DocumentationDoc;
    } catch (error) {
      console.error('[DocumentationService] Error fetching doc:', error);
      throw error;
    }
  }

  /**
   * Get a documentation doc by toolId and docId
   * This is useful when you know the logical identifiers but not the Firestore ID
   */
  static async getDocByDocId(
    toolId: ToolId,
    docId: string
  ): Promise<DocumentationDoc | null> {
    try {
      const { db, helpers } = await getFirebase();
      const { collection, query, where, getDocs } = helpers;
      const q = query(
        collection(db, COLLECTION_NAME),
        where('toolId', '==', toolId),
        where('docId', '==', docId)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const docSnap = snapshot.docs[0];
      return {
        id: docSnap.id,
        ...convertTimestamps(docSnap.data()),
      } as DocumentationDoc;
    } catch (error) {
      console.error('[DocumentationService] Error fetching doc by docId:', error);
      throw error;
    }
  }

  /**
   * Get documentation docs that match a specific context trigger.
   * Uses Firestore array-contains query on contextTriggers field.
   *
   * @param context - The context trigger to match (e.g., "episode-composer:draft")
   * @returns Array of matching docs, sorted by sortOrder
   */
  static async getByContext(context: string): Promise<DocumentationDoc[]> {
    try {
      const { db, helpers } = await getFirebase();
      const { collection, query, where, orderBy, getDocs } = helpers;
      const q = query(
        collection(db, COLLECTION_NAME),
        where('contextTriggers', 'array-contains', context),
        orderBy('sortOrder', 'asc')
      );

      const snapshot = await getDocs(q);

      return snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...convertTimestamps(docSnap.data()),
      })) as DocumentationDoc[];
    } catch (error) {
      console.error('[DocumentationService] Error fetching by context:', error);
      throw error;
    }
  }

  /**
   * Get all documentation docs for a specific tool.
   * Used for browse mode.
   *
   * @param toolId - The tool identifier (e.g., "episode-composer")
   * @returns Array of all docs for the tool, sorted by sortOrder
   */
  static async getAllForTool(toolId: ToolId): Promise<DocumentationDoc[]> {
    try {
      const { db, helpers } = await getFirebase();
      const { collection, query, where, orderBy, getDocs } = helpers;
      const q = query(
        collection(db, COLLECTION_NAME),
        where('toolId', '==', toolId),
        orderBy('sortOrder', 'asc')
      );

      const snapshot = await getDocs(q);

      return snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...convertTimestamps(docSnap.data()),
      })) as DocumentationDoc[];
    } catch (error) {
      console.error('[DocumentationService] Error fetching all for tool:', error);
      throw error;
    }
  }

  /**
   * Get documentation docs for a specific tool and category.
   *
   * @param toolId - The tool identifier
   * @param category - The category to filter by
   * @returns Array of matching docs, sorted by sortOrder
   */
  static async getByCategory(
    toolId: ToolId,
    category: DocumentationCategory
  ): Promise<DocumentationDoc[]> {
    try {
      const { db, helpers } = await getFirebase();
      const { collection, query, where, orderBy, getDocs } = helpers;
      const q = query(
        collection(db, COLLECTION_NAME),
        where('toolId', '==', toolId),
        where('category', '==', category),
        orderBy('sortOrder', 'asc')
      );

      const snapshot = await getDocs(q);

      return snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...convertTimestamps(docSnap.data()),
      })) as DocumentationDoc[];
    } catch (error) {
      console.error('[DocumentationService] Error fetching by category:', error);
      throw error;
    }
  }

  /**
   * Get all documentation for a tool, grouped by category.
   * Used for browse mode display.
   *
   * @param toolId - The tool identifier
   * @returns Array of categories with their docs, in display order
   */
  static async getAllGroupedByCategory(
    toolId: ToolId
  ): Promise<DocumentationByCategory[]> {
    try {
      // Fetch all docs for the tool
      const allDocs = await this.getAllForTool(toolId);

      // Group by category
      const docsByCategory = new Map<DocumentationCategory, DocumentationDoc[]>();

      allDocs.forEach((doc) => {
        const existing = docsByCategory.get(doc.category) || [];
        existing.push(doc);
        docsByCategory.set(doc.category, existing);
      });

      // Build result in category display order
      const result: DocumentationByCategory[] = [];

      CATEGORY_ORDER.forEach((category) => {
        const docs = docsByCategory.get(category);
        if (docs && docs.length > 0) {
          result.push({
            category,
            label: CATEGORY_LABELS[category],
            docs: docs.sort((a, b) => a.sortOrder - b.sortOrder),
          });
        }
      });

      return result;
    } catch (error) {
      console.error('[DocumentationService] Error fetching grouped by category:', error);
      throw error;
    }
  }

  /**
   * Build a context trigger string.
   * Helper to ensure consistent format.
   *
   * @param toolId - The tool identifier
   * @param context - The context (e.g., status, screen name)
   * @returns Formatted context trigger string
   */
  static buildContextTrigger(toolId: ToolId, context: string): string {
    return `${toolId}:${context}`;
  }

  /**
   * Build context trigger for Episode Composer status.
   * Convenience method for the most common use case.
   *
   * @param status - The episode status (e.g., "draft", "scripts_pending_review")
   * @returns Formatted context trigger string
   */
  static buildEpisodeStatusTrigger(status: string): string {
    return this.buildContextTrigger('episode-composer', status.toLowerCase());
  }

  // ===== Release Notes & Spotlights =====

  /**
   * Get active release notes and spotlights for a context trigger.
   * Returns docs that are:
   * - In release-note or spotlight category
   * - Currently active (isActive = true)
   * - Within display window (based on createdAt + displayDays)
   */
  static async getActiveReleaseNotesForContext(context: string): Promise<DocumentationDoc[]> {
    try {
      const { db, helpers } = await getFirebase();
      const { collection, query, where, getDocs } = helpers;

      // Query for active docs with this context trigger
      const q = query(
        collection(db, COLLECTION_NAME),
        where('contextTriggers', 'array-contains', context),
        where('isActive', '==', true)
      );

      const snapshot = await getDocs(q);
      const now = new Date();
      const DEFAULT_DISPLAY_DAYS = 5;

      const docs = snapshot.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...convertTimestamps(docSnap.data()),
        })) as DocumentationDoc[];

      // Filter by category, display type, and display window
      return docs
        .filter((doc) => {
          // Only release-note and spotlight categories
          if (doc.category !== 'release-note' && doc.category !== 'spotlight') {
            return false;
          }

          // Only banner display type (or undefined which defaults to banner)
          if (doc.displayType && doc.displayType !== 'banner') {
            return false;
          }

          // Check if we're past the start date (activeFrom)
          const startDate = doc.activeFrom || doc.createdAt;
          if (startDate && startDate > now) {
            return false; // Not yet active
          }

          // Check display window based on activeFrom (or createdAt) + displayDays
          const displayDays = doc.displayDays ?? DEFAULT_DISPLAY_DAYS;
          if (startDate) {
            const endDate = new Date(startDate);
            endDate.setDate(endDate.getDate() + displayDays);
            if (endDate < now) {
              return false; // Display window has passed
            }
          }

          return true;
        })
        // Sort by priority (critical first) then by createdAt (newest first)
        .sort((a, b) => {
          const priorityOrder: Record<string, number> = { critical: 0, high: 1, normal: 2, low: 3 };
          const aPriority = priorityOrder[a.priority || 'normal'];
          const bPriority = priorityOrder[b.priority || 'normal'];
          if (aPriority !== bPriority) {
            return aPriority - bPriority;
          }
          return (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0);
        });
    } catch (error) {
      console.error('[DocumentationService] Error fetching active release notes:', error);
      return [];
    }
  }

  /**
   * Check if a user has dismissed a specific doc.
   */
  static async isDismissed(userId: string, documentId: string): Promise<boolean> {
    try {
      const { db, helpers } = await getFirebase();
      const { doc, getDoc } = helpers;

      const dismissedRef = doc(db, 'users', userId, 'dismissedDocs', documentId);
      const dismissedSnap = await getDoc(dismissedRef);
      return dismissedSnap.exists();
    } catch (error) {
      console.error('[DocumentationService] Error checking dismissed status:', error);
      return false;
    }
  }

  /**
   * Get all dismissed doc IDs for a user.
   */
  static async getDismissedDocIds(userId: string): Promise<string[]> {
    try {
      const { db, helpers } = await getFirebase();
      const { collection, getDocs } = helpers;

      const dismissedCollection = collection(db, 'users', userId, 'dismissedDocs');
      const snapshot = await getDocs(dismissedCollection);
      return snapshot.docs.map((d) => d.id);
    } catch (error) {
      console.error('[DocumentationService] Error getting dismissed docs:', error);
      return [];
    }
  }

  /**
   * Dismiss a doc for a user.
   */
  static async dismissDoc(userId: string, documentId: string): Promise<void> {
    try {
      const { db, helpers } = await getFirebase();
      const { doc, setDoc, serverTimestamp } = helpers;

      const dismissedRef = doc(db, 'users', userId, 'dismissedDocs', documentId);
      await setDoc(dismissedRef, {
        dismissedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('[DocumentationService] Error dismissing doc:', error);
      throw error;
    }
  }

  /**
   * Get active release notes for a context that the user hasn't dismissed.
   * Returns only the highest priority undismissed doc (for single banner display).
   *
   * @deprecated Use getUndismissedSpotlightForContext instead - spotlights now come from releases
   */
  static async getUndismissedReleaseNoteForContext(
    userId: string,
    context: string
  ): Promise<DocumentationDoc | null> {
    // First try to get a spotlight from releases or standalone spotlights
    const spotlight = await this.getUndismissedSpotlightForContext(userId, context);
    if (spotlight) {
      // Convert spotlight to DocumentationDoc format for backward compatibility
      // Include color, icon, and isStandalone as extra properties for FeatureBanner to use
      return {
        // For standalone spotlights, just use the id directly
        // For release spotlights, use releaseId_spotlightId format
        id: spotlight.isStandalone ? spotlight.id : (spotlight.releaseId + '_' + spotlight.id),
        toolId: 'episode-composer' as ToolId,
        docId: spotlight.id,
        title: spotlight.title,
        subtitle: undefined,
        content: spotlight.description,
        category: 'spotlight' as DocumentationCategory,
        sortOrder: 0,
        contextTriggers: spotlight.contextTriggers,
        isActive: true,
        priority: 'normal',
        displayType: 'banner',
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: '',
        updatedBy: '',
        // Pass through color, icon, standalone flag, release version, and category for FeatureBanner
        spotlightColor: spotlight.color,
        spotlightIcon: spotlight.icon,
        isStandalone: spotlight.isStandalone,
        releaseVersion: spotlight.releaseVersion,
        spotlightCategory: spotlight.category,
      } as DocumentationDoc & { spotlightColor?: string; spotlightIcon?: string; isStandalone?: boolean; releaseVersion?: string; spotlightCategory?: string };
    }

    // Fall back to old documentation-based spotlights
    try {
      const activeDocs = await this.getActiveReleaseNotesForContext(context);

      if (activeDocs.length === 0) {
        return null;
      }

      const dismissedIds = await this.getDismissedDocIds(userId);
      const dismissedSet = new Set(dismissedIds);

      const undismissed = activeDocs.find((doc) => !dismissedSet.has(doc.id));
      return undismissed || null;
    } catch (error) {
      console.error('[DocumentationService] Error getting undismissed release note:', error);
      return null;
    }
  }

  // ===== New Release-Based Spotlights =====

  /**
   * Spotlight from a release with its release context
   */
  static SpotlightWithRelease: {
    id: string;
    title: string;
    description: string;
    contextTriggers: string[];
    releaseId: string;
    releaseVersion: string;
  };

  /**
   * Get active spotlights from published releases AND standalone spotlights for a context trigger.
   * Priority: Release spotlights are shown first, then standalone spotlights.
   */
  static async getActiveSpotlightsForContext(
    context: string
  ): Promise<Array<{
    id: string;
    title: string;
    description: string;
    contextTriggers: string[];
    releaseId: string;
    releaseVersion: string;
    color?: string;
    icon?: string;
    isStandalone: boolean;
    category?: string;
  }>> {
    try {
      const { db, helpers } = await getFirebase();
      const { collection, query, where, getDocs } = helpers;

      const spotlights: Array<{
        id: string;
        title: string;
        description: string;
        contextTriggers: string[];
        releaseId: string;
        releaseVersion: string;
        color?: string;
        icon?: string;
        isStandalone: boolean;
        category?: string;
      }> = [];

      // 1. Query for published releases (existing behavior)
      const releasesQuery = query(
        collection(db, 'releases'),
        where('status', '==', 'published')
      );

      const releasesSnapshot = await getDocs(releasesQuery);

      releasesSnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const releaseSpotlights = data.spotlights || [];

        releaseSpotlights.forEach((spotlight: any) => {
          // Check if this spotlight matches the context
          if (spotlight.contextTriggers?.includes(context)) {
            spotlights.push({
              id: spotlight.id,
              title: spotlight.title,
              description: spotlight.description,
              contextTriggers: spotlight.contextTriggers,
              releaseId: docSnap.id,
              releaseVersion: data.version,
              color: spotlight.color,
              icon: spotlight.icon,
              isStandalone: false,
            });
          }
        });
      });

      // 2. Query for standalone spotlights (NEW)
      const standaloneQuery = query(
        collection(db, 'spotlights'),
        where('status', '==', 'published')
      );

      const standaloneSnapshot = await getDocs(standaloneQuery);
      const now = new Date();
      const DEFAULT_DISPLAY_DAYS = 30;

      standaloneSnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();

        // Check context triggers
        if (!data.contextTriggers?.includes(context)) {
          return;
        }

        // Convert timestamps using duck typing
        const toDate = (val: any): Date | null => {
          if (!val) return null;
          if (val.toDate) return val.toDate();
          if (val instanceof Date) return val;
          return null;
        };

        const publishedAt = toDate(data.publishedAt);
        const createdAt = toDate(data.createdAt);
        const activeFrom = toDate(data.activeFrom);
        const activeUntil = toDate(data.activeUntil);

        // Check activeFrom - spotlight won't show until this date
        const startDate = activeFrom || publishedAt || createdAt;
        if (startDate && startDate > now) {
          return; // Not yet active
        }

        // Check activeUntil - if set, use it directly
        if (activeUntil && activeUntil < now) {
          return; // Past end date
        }

        // If no activeUntil, calculate end date from startDate + displayDays
        if (!activeUntil && startDate) {
          const displayDays = data.displayDays ?? DEFAULT_DISPLAY_DAYS;
          const endDate = new Date(startDate);
          endDate.setDate(endDate.getDate() + displayDays);
          if (endDate < now) {
            return; // Display window has passed
          }
        }

        spotlights.push({
          id: docSnap.id,
          title: data.title,
          description: data.description,
          contextTriggers: data.contextTriggers,
          releaseId: 'standalone', // Special marker for standalone spotlights
          releaseVersion: '',
          color: data.color,
          icon: data.icon,
          isStandalone: true,
          category: data.category,
        });
      });

      // Sort: Release spotlights first, then standalone
      // This ensures release-based spotlights are shown before standalone ones
      return spotlights.sort((a, b) => {
        if (a.isStandalone === b.isStandalone) return 0;
        return a.isStandalone ? 1 : -1;
      });
    } catch (error) {
      console.error('[DocumentationService] Error fetching active spotlights:', error);
      return [];
    }
  }

  /**
   * Get dismissed spotlight IDs for a user.
   * Spotlights are dismissed by releaseId_spotlightId combination.
   */
  static async getDismissedSpotlightIds(userId: string): Promise<string[]> {
    try {
      const { db, helpers } = await getFirebase();
      const { collection, getDocs } = helpers;

      const dismissedCollection = collection(db, 'users', userId, 'dismissedSpotlights');
      const snapshot = await getDocs(dismissedCollection);
      return snapshot.docs.map((d) => d.id);
    } catch (error) {
      console.error('[DocumentationService] Error getting dismissed spotlights:', error);
      return [];
    }
  }

  /**
   * Dismiss a spotlight for a user.
   */
  static async dismissSpotlight(userId: string, releaseId: string, spotlightId: string): Promise<void> {
    try {
      const { db, helpers } = await getFirebase();
      const { doc, setDoc, serverTimestamp } = helpers;

      const dismissedRef = doc(db, 'users', userId, 'dismissedSpotlights', `${releaseId}_${spotlightId}`);
      await setDoc(dismissedRef, {
        dismissedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('[DocumentationService] Error dismissing spotlight:', error);
      throw error;
    }
  }

  /**
   * Get the first undismissed spotlight for a context.
   * Priority: Release spotlights shown first, then standalone spotlights.
   * Dismissal format:
   *   - Release spotlights: {releaseId}_{spotlightId}
   *   - Standalone spotlights: standalone_{spotlightId}
   */
  static async getUndismissedSpotlightForContext(
    userId: string,
    context: string
  ): Promise<{
    id: string;
    title: string;
    description: string;
    contextTriggers: string[];
    releaseId: string;
    releaseVersion: string;
    color?: string;
    icon?: string;
    isStandalone?: boolean;
    category?: string;
  } | null> {
    try {
      const spotlights = await this.getActiveSpotlightsForContext(context);

      if (spotlights.length === 0) {
        return null;
      }

      const dismissedIds = await this.getDismissedSpotlightIds(userId);
      const dismissedSet = new Set(dismissedIds);

      // Find first undismissed spotlight
      // Spotlights are already sorted with release spotlights first
      const undismissed = spotlights.find((s) => {
        // Build dismissal key based on spotlight type
        const dismissalKey = s.isStandalone
          ? `standalone_${s.id}`
          : `${s.releaseId}_${s.id}`;
        return !dismissedSet.has(dismissalKey);
      });
      return undismissed || null;
    } catch (error) {
      console.error('[DocumentationService] Error getting undismissed spotlight:', error);
      return null;
    }
  }
}
